<template>
  <div>
    <v-card class="sf-card sf-design"
            flat>
      <div class="sf-card__header">
        <div>
          <h2 class="sf-card__title">{{ $t('preview') }}</h2>
          <p class="sf-card__subtitle">{{ usesSampleProducts ? $t('previewHintSample') : $tc('previewHintReal', previewProducts.length, { count: previewProducts.length }) }}</p>
        </div>
        <div class="sf-card__tools">
          <v-menu offset-y
                  left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="primary"
                     depressed
                     v-bind="attrs"
                     v-on="on">
                <v-icon left>ti-pencil</v-icon>
                {{ $t('edit') }}
                <v-icon right>ti-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item v-if="!hasBackground"
                           v-on:click="chooseBackground">
                <v-list-item-icon class="mr-3"><v-icon>ti-photo</v-icon></v-list-item-icon>
                <v-list-item-title>{{ $t('addBackground') }}</v-list-item-title>
              </v-list-item>
              <v-list-item v-else
                           v-on:click="removeBackground">
                <v-list-item-icon class="mr-3"><v-icon>ti-photo-x</v-icon></v-list-item-icon>
                <v-list-item-title>{{ $t('removeBackground') }}</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="!hasLogo" v-on:click="chooseLogo">
                <v-list-item-icon class="mr-3"><v-icon>ti-shape</v-icon></v-list-item-icon>
                <v-list-item-title>{{ $t('addLogo') }}</v-list-item-title>
              </v-list-item>
              <v-list-item v-else
                           v-on:click="removeLogo">
                <v-list-item-icon class="mr-3"><v-icon>ti-circle-x</v-icon></v-list-item-icon>
                <v-list-item-title>{{ $t('removeLogo') }}</v-list-item-title>
              </v-list-item>
              <v-divider class="my-1" />
              <v-list-item>
                <v-list-item-action class="mr-3">
                  <v-checkbox color="primary"
                              hide-details="auto"
                              v-model="supplier.darkBackground" />
                </v-list-item-action>
                <v-list-item-title>{{ $t('darkBackground') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <v-card-text class="sf-card__body">
        <top-image-header v-bind:image-src="supplier.backgroundId != null ? backgroundUrl : '/images/headertemp.jpg'"
                          v-bind:gradient="supplier.darkBackground && supplier.backgroundId == null ? 'to left, rgba(17, 62, 124, 0.95), rgba(17, 62, 124, 0.9)' : 'to left, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.9)'">
          <v-btn v-bind:dark="supplier.darkBackground"
                 exact
                 slot="left-button"
                 text>
            <v-icon left>ti-arrow-left</v-icon>
            {{ $t('back') }}
          </v-btn>
          <v-btn color="accent"
                 depressed
                 exact
                 slot="right-button">
            <v-icon left>ti-check</v-icon>
            {{ $t('confirmOrder') }}
          </v-btn>
          <div v-if="supplier.logoId != null">
            <img class="sf-hero__logo"
                 v-bind:src="logoUrl">
          </div>
          <h1 v-else v-bind:class="[ 'sf-hero__title', supplier.darkBackground ? 'white--text' : 'primary--text' ]">{{ supplier.supplierGroup | defaultValue($t('supplier')) }}</h1>
        </top-image-header>

        <v-textarea class="mt-6"
                    auto-grow
                    hide-details="auto"
                    outlined
                    rows="5"
                    v-bind:label="$t('pleaseWriteAShortIntroduction')"
                    v-model="supplier.description" />

        <div class="mt-6">
          <product-sheet-card preview
                              ref="productSheetCard"
                              v-bind:editable-images="!usesSampleProducts"
                              v-bind:invitation-code="invitationCode"
                              v-bind:loading="false"
                              v-bind:products="previewProducts"
                              v-bind:supplier="supplier" />
        </div>
      </v-card-text>
    </v-card>

    <input accept="image/png, image/jpeg"
           class="hidden"
           ref="backgroundPicker"
           type="file"
           v-on:change="setBackground">
    <input accept="image/png, image/jpeg"
           class="hidden"
           ref="logoPicker"
           type="file"
           v-on:change="setLogo">
  </div>
</template>

<script>
  import settings from '../../settings'

  import TopImageHeader from '@/components/common/TopImageHeader'
  import ProductSheetCard from '@/components/ProductSheetCard'

  export default {
    components: {
      TopImageHeader,
      ProductSheetCard
    },
    computed: {
      hasBackground() {
        return this.supplier.backgroundId != null
      },
      hasLogo() {
        return this.supplier.logoId != null
      },
      backgroundUrl() {
        return `${settings.apiUri}/attachment/${this.supplier.backgroundId}/content`
      },
      logoUrl() {
        return `${settings.apiUri}/attachment/${this.supplier.logoId}/content`
      },
      previewProducts() {
        return this.usesSampleProducts ? this.sampleProducts : this.products
      },
      usesSampleProducts() {
        return this.products == null || this.products.length === 0
      },
      sampleProducts() {
        return [
          {
            id: "123456",
            title: "Bierkasten 20x0,33",
            packaging: "Kasten",
            packagingSize: "20 Flaschen",
            discounts: [ { deliveryPeriod: 0, productId: "123456", value: "-1,50 €" }, { deliveryPeriod: 1, productId: "123456", value: "-1,50 €" } ],
            orders: [],
            newText1: "Beispiel-Artikel – dient rein zur Illustration und wird mit Ihren Artikeln ersetzt!",
            price: 9.99,
            quantity: [ 0, 0 ],
            orderQuantity: [ 0, 0 ]
          },
          {
            id: "123457",
            title: "Bierfass 10 l",
            packaging: "Fass",
            packagingSize: "10 l",
            discounts: [ { deliveryPeriod: 0, productId: "123457", value: "-1,50 €" }, { deliveryPeriod: 1, productId: "123457", value: "-1,50 €" } ],
            orders: [],
            newText1: "Beispiel-Artikel – dient rein zur Illustration und wird mit Ihren Artikeln ersetzt!",
            price: 9.99,
            quantity: [ 0, 0 ],
            orderQuantity: [ 0, 0 ]
          }
        ]
      }
    },
    data: () => ({
      //darkBackground: true,
      //backgroundId: null,
      //logoId: null
    }),
    methods: {
      chooseBackground() {
        this.$refs.backgroundPicker.click()
      },
      chooseLogo() {
        this.$refs.logoPicker.click()
      },
      async setBackground(e) {
        this.supplier.backgroundId = await this.uploadAttachment(e)
      },
      async setLogo(e) {
        this.supplier.logoId = await this.uploadAttachment(e)
      },
      removeBackground() {
        this.supplier.backgroundId = null
      },
      removeLogo() {
        this.supplier.logoId = null
      },
      async uploadAttachment(e) {
        const file = e.target.files[0]

        if (file == null) {
          return
        }

        const data = new FormData()
        data.append('file', file)

        const upload = await this.$store.dispatch('createAttachment', { data })

        return upload.id
      }
    },
    name: 'DesignCard',
    props: {
      form: Object,
      /** Einladungscode des Lieferanten (Bild-Upload); in der Administration leer, dann greift das Access-Token. */
      invitationCode: String,
      products: Array,
      supplier: Object
    }
  }
</script>

<style scoped>
  .hidden {
    display: none;
  }

  .sf-design >>> .sf-card__body {
    background: var(--sf-surface-muted);
    border-radius: 0 0 16px 16px;
  }
</style>

<i18n>
  {
    "de": {
      "aboutUs": "Über uns",
      "addBackground": "Hintergrundbild hinzufügen…",
      "addLogo": "Logo hinzufügen…",
      "darkBackground": "Dunkler Hintergrund",
      "edit": "Bearbeiten",
      "pleaseWriteAShortIntroduction": "Bitte schreiben Sie eine kurze Einleitung",
      "removeBackground": "Hintergrundbild entfernen",
      "removeLogo": "Logo entfernen",
      "back": "Zurück",
      "confirmOrder": "Bestellung aufgeben",
      "supplier": "Lieferant",
      "preview": "Vorschau Ihres Ordersatzes",
      "previewHintSample": "Noch keine Artikel hinterlegt – die gezeigten Beispielartikel dienen nur zur Illustration.",
      "previewHintReal": "{count} Artikel | {count} Artikel aus Ihrem Ordersatz – so sehen Kunden ihn während der Messe. Klicken Sie auf ein Artikelbild, um es hochzuladen oder zu ersetzen. | {count} Artikel aus Ihrem Ordersatz – so sehen Kunden ihn während der Messe. Klicken Sie auf ein Artikelbild, um es hochzuladen oder zu ersetzen."
    },
    "en": {
      "aboutUs": "About us",
      "addBackground": "Add background…",
      "addLogo": "Add logo…",
      "darkBackground": "Dark background",
      "edit": "Edit",
      "pleaseWriteAShortIntroduction": "Please write a short introduction",
      "removeBackground": "Remove background",
      "removeLogo": "Remove logo",
      "back": "Back",
      "confirmOrder": "Confirm order",
      "supplier": "Supplier",
      "preview": "Preview of your order sheet",
      "previewHintSample": "No products yet – the sample products shown are for illustration only.",
      "previewHintReal": "{count} products | {count} product from your order sheet – this is how customers will see it during the fair. Click a product image to upload or replace it. | {count} products from your order sheet – this is how customers will see it during the fair. Click a product image to upload or replace it."
    }
  }
</i18n>